import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ImportDecision, ImportPreviewRow } from '../logic/note-json-import.logic';

/**
 * Review dialog for "Import JSON Note(s)...": lists every note found in the
 * selected file with its status (new / already exists / invalid), and lets
 * the user resolve every _id conflict in one pass — either individually per
 * row, or in bulk ("Overwrite all conflicts" / "Skip all conflicts") — so
 * importing many notes never means answering a popup once per note.
 */
@Component({
  selector: 'app-import-preview-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule, SelectModule, TableModule],
  template: `
    <p-dialog
      header="Import JSON Note(s)..."
      [(visible)]="visibleModel"
      [modal]="true"
      [style]="{ width: 'min(96vw, 48rem)' }"
      [draggable]="false"
      [resizable]="false"
      appendTo="body"
      (onHide)="onCancel()"
    >
      <div *ngIf="fileErrors.length" class="mb-3 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
        <div class="font-semibold">This file could not be imported:</div>
        <ul class="list-disc pl-5">
          <li *ngFor="let err of fileErrors">{{ err }}</li>
        </ul>
      </div>

      <ng-container *ngIf="!fileErrors.length">
        <div class="mb-2 text-sm text-surface-600">
          {{ rows.length }} note(s) found — {{ newCount }} new, {{ conflictCount }} already exist{{
            invalidCount ? ', ' + invalidCount + ' invalid (will be skipped)' : ''
          }}.
        </div>

        <div *ngIf="conflictCount > 0" class="mb-3 flex items-center gap-2">
          <span class="text-sm text-surface-600">For all notes that already exist:</span>
          <p-button label="Overwrite all" size="small" severity="warn" (onClick)="setAllConflicts('overwrite')"></p-button>
          <p-button label="Skip all" size="small" severity="secondary" (onClick)="setAllConflicts('skip')"></p-button>
        </div>

        <div class="max-h-[50vh] overflow-y-auto">
          <p-table [value]="rows" [scrollable]="true">
            <ng-template pTemplate="header">
              <tr>
                <th>Title</th>
                <th style="width: 9rem">Status</th>
                <th style="width: 11rem">Action</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td class="truncate" [title]="row.record?.Title">
                  {{ row.record?.Title || '(untitled)' }}
                  <div *ngIf="row.errors.length" class="text-xs text-red-600">{{ row.errors.join(' ') }}</div>
                </td>
                <td>
                  <span
                    class="rounded px-2 py-0.5 text-xs"
                    [ngClass]="{
                      'bg-green-100 text-green-800': row.status === 'new',
                      'bg-yellow-100 text-yellow-800': row.status === 'conflict',
                      'bg-red-100 text-red-800': row.status === 'invalid'
                    }"
                  >
                    {{ row.status === 'new' ? 'New' : row.status === 'conflict' ? 'Already exists' : 'Invalid' }}
                  </span>
                </td>
                <td>
                  <p-select
                    *ngIf="row.status === 'conflict'; else fixedDecision"
                    [options]="conflictDecisionOptions"
                    optionLabel="label"
                    optionValue="value"
                    [(ngModel)]="row.decision"
                    appendTo="body"
                    class="w-full"
                  ></p-select>
                  <ng-template #fixedDecision>
                    <span class="text-xs text-surface-500">{{ row.status === 'new' ? 'Import' : 'Skip' }}</span>
                  </ng-template>
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>
      </ng-container>

      <ng-template pTemplate="footer">
        <p-button label="View Import Log" severity="secondary" [text]="true" icon="pi pi-list" (onClick)="onViewLog()"></p-button>
        <p-button label="Cancel" severity="secondary" (onClick)="onCancel()"></p-button>
        <p-button
          *ngIf="!fileErrors.length"
          [label]="'Import ' + importCount + ' note(s)'"
          [disabled]="importCount === 0"
          (onClick)="onConfirm()"
        ></p-button>
      </ng-template>
    </p-dialog>
  `,
})
export class ImportPreviewDialogComponent {
  @Input() visible = false;
  @Input() fileErrors: string[] = [];
  @Input() rows: ImportPreviewRow[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() cancel = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<ImportPreviewRow[]>();
  @Output() viewLog = new EventEmitter<void>();

  readonly conflictDecisionOptions: { label: string; value: ImportDecision }[] = [
    { label: 'Overwrite', value: 'overwrite' },
    { label: 'Skip', value: 'skip' },
  ];

  get visibleModel(): boolean {
    return this.visible;
  }
  set visibleModel(value: boolean) {
    this.visible = value;
    this.visibleChange.emit(value);
    if (!value) this.cancel.emit();
  }

  get newCount(): number {
    return this.rows.filter((r) => r.status === 'new').length;
  }
  get conflictCount(): number {
    return this.rows.filter((r) => r.status === 'conflict').length;
  }
  get invalidCount(): number {
    return this.rows.filter((r) => r.status === 'invalid').length;
  }
  get importCount(): number {
    return this.rows.filter((r) => r.decision === 'import' || r.decision === 'overwrite').length;
  }

  setAllConflicts(decision: ImportDecision): void {
    this.rows.forEach((row) => {
      if (row.status === 'conflict') row.decision = decision;
    });
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onConfirm(): void {
    this.confirm.emit(this.rows);
  }

  onViewLog(): void {
    this.viewLog.emit();
  }
}
